import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, Loader2, RotateCcw } from 'lucide-react';
import {
  TicketsWrapper,
  TicketsContainer,
  PageHeader,
  HeaderLeft,
  TitleRow,
  Title,
  TotalBadge,
  Description,
  RaiseTicketButton,
  ToolbarCard,
  ToolbarRow,
  TableCard,
  TicketIdCell,
  UnviewedDot,
  UnviewedCount,
} from './Tickets.styles';
import SearchBar from './components/SearchBar/SearchBar';
import FilterBar, { CloseIcon } from './components/FilterBar/FilterBar';
import CreateTicketModal from './modals/CreateTicketModal';
import { useTicketFilters } from './hooks/useTicketFilters';
import { useTicketsQuery } from './hooks/useTicketsQuery';

import {
  MonoText,
  Pill,
  StatusBadge,
  StatusDot,
} from '../../../components/uielements/table/table.styles';
import { Table } from '../../../components/uielements/table';
import {
  ActiveFiltersLabel,
  ActiveFiltersRow,
  FilterTag,
  FilterTagValue,
  RemoveFilterButton,
  ResetAllButton,
} from './components/FilterBar/filterBar.styles';
import {
  ActionButtons,
  OutlineButon,
  EmptyState,
  RetryButton,
  SpinWrapper,
} from '../../../settings/appStyle/global.style';
import { formatDateTime } from '../../../helper/dateRelated';
import { useAuth } from '../../../hooks/useAuth';
import TruncatedTitleCell from '../../../components/uielements/TruncatedTitleCell';

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 400;

const FILTER_LABELS = { status: 'Status', type: 'Type', startDate: 'From', endDate: 'To' };
const STATUS_VALUE_LABELS = { OPEN: 'Open', CLOSED: 'Closed' };
const TYPE_VALUE_LABELS = { BUG: 'Bug', QUERY: 'Query', OTHER: 'Other' };

const formatFilterValue = (key, value) => {
  if (key === 'status') return STATUS_VALUE_LABELS[value] || value;
  if (key === 'type') return TYPE_VALUE_LABELS[value] || value;
  return value;
};

const Tickets = () => {
  const navigate = useNavigate();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const { councilInfo } = useAuth();

  // URL is the single source of truth for search/filters/page.
  const { search, filters, page, setSearch, setFilters, removeFilter, resetFilters, setPage } =
    useTicketFilters();

  const [searchInput, setSearchInput] = useState(search);
  useEffect(() => setSearchInput(search), [search]);
  useEffect(() => {
    if (searchInput === search) return undefined;
    const timeout = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const queryParams = useMemo(
    () => ({
      search,
      status: filters.status,
      type: filters.type,
      startDate: filters.startDate,
      endDate: filters.endDate,
      page,
      pageSize: PAGE_SIZE,
    }),
    [search, filters, page]
  );

  const { data, isLoading, isFetching, isError, error, refetch } = useTicketsQuery(queryParams);
  const rows = data?.tickets ?? [];
  const total = data?.totalTickets ?? 0;

  const folderName = rows[0]?.folderName || councilInfo?.folderName;

  const activeFilters = useMemo(
    () =>
      Object.entries(filters)
        .filter(([, value]) => value)
        .map(([key, value]) => ({
          key,
          label: FILTER_LABELS[key],
          value: formatFilterValue(key, value),
        })),
    [filters]
  );

  const handleDashboard = () => navigate('/');
  const handleRowClick = (ticket) => navigate(`/ticket/${ticket._id}`);

  const handleOpenCreateModal = () => setIsCreateModalOpen(true);
  const handleCloseCreateModal = () => setIsCreateModalOpen(false);

  const columns = useMemo(
    () => [
      {
        Header: 'Ticket ID',
        accessor: 'ticketCode',
        width: '20%',
        disableSortBy: true,
        Cell: ({ value, row }) => {
          const unviewed = row?.original?.unviewedQueries ?? 0;
          return (
            <TicketIdCell>
              <MonoText>#{value}</MonoText>
              {unviewed > 0 && (
                <UnviewedDot role="status" aria-label={`${unviewed} unviewed`} title="Unviewed">
                  <UnviewedCount>{unviewed > 9 ? '9+' : unviewed}</UnviewedCount>
                </UnviewedDot>
              )}
            </TicketIdCell>
          );
        },
      },
      {
        Header: 'Title & summary',
        accessor: 'title',
        width: '32%',
        Cell: ({ value }) => <TruncatedTitleCell text={value} />,
      },
      {
        Header: 'Category',
        accessor: 'ticketType',
        width: '12%',
        Cell: ({ value }) => <Pill>{value}</Pill>,
      },
      {
        Header: 'Status',
        accessor: 'status',
        width: '14%',
        align: 'center',
        Cell: ({ value }) => (
          <StatusBadge $status={value}>
            <StatusDot />
            {value}
          </StatusBadge>
        ),
      },
      {
        Header: 'Created',
        accessor: 'createdAt',
        width: '16%',
        Cell: ({ value }) => <span>{formatDateTime(value)}</span>,
      },
    ],
    []
  );

  return (
    <TicketsWrapper>
      <TicketsContainer>
        <PageHeader>
          <HeaderLeft>
            <TitleRow>
              <Title>Your tickets</Title>
              <TotalBadge>{total} Total</TotalBadge>
            </TitleRow>
            <Description>
              Search, filter and manage support requests across all internal queues
            </Description>
          </HeaderLeft>
          <ActionButtons>
            <OutlineButon onClick={handleDashboard} type="button">
              Dashboard
            </OutlineButon>
            <RaiseTicketButton type="button" onClick={handleOpenCreateModal}>
              <PlusIcon size={16} />
              Raise a ticket
            </RaiseTicketButton>
          </ActionButtons>
        </PageHeader>

        <ToolbarCard>
          <ToolbarRow>
            <SearchBar
              value={searchInput}
              onChange={setSearchInput}
              placeholder="Search tickets by title"
            />
            <FilterBar
              activeFilters={activeFilters}
              appliedFilters={filters}
              onApplyFilters={setFilters}
              onResetFilters={resetFilters}
            />
          </ToolbarRow>

          {activeFilters.length > 0 && (
            <ActiveFiltersRow>
              <ActiveFiltersLabel>Active filters:</ActiveFiltersLabel>
              {activeFilters.map((filter) => (
                <FilterTag key={filter.key}>
                  <span>{filter.label}:</span>
                  <FilterTagValue>{filter.value}</FilterTagValue>
                  <RemoveFilterButton
                    type="button"
                    aria-label={`Remove ${filter.label} filter`}
                    onClick={() => removeFilter(filter.key)}
                  >
                    <CloseIcon />
                  </RemoveFilterButton>
                </FilterTag>
              ))}
              <ResetAllButton type="button" onClick={resetFilters}>
                Reset all
              </ResetAllButton>
            </ActiveFiltersRow>
          )}
        </ToolbarCard>

        <TableCard>
          {isLoading ? (
            <EmptyState>
              <SpinWrapper>
                <Loader2 />
              </SpinWrapper>
              Loading your tickets...
            </EmptyState>
          ) : isError ? (
            <EmptyState role="alert">
              Couldn't load tickets{error?.message ? `: ${error.message}` : ''}. Please try again
              later.
              <RetryButton type="button" onClick={() => refetch()} disabled={isFetching}>
                <RotateCcw />
                {isFetching ? 'Retrying…' : 'Retry'}
              </RetryButton>
            </EmptyState>
          ) : (
            <Table
              data={rows}
              columns={columns}
              hasFooter
              page={page - 1}
              pageSize={PAGE_SIZE}
              total={total}
              isLoading={isLoading}
              isFetching={isFetching}
              prevPage={() => setPage(Math.max(page - 1, 1))}
              nextPage={() => setPage(page + 1)}
              onRowClick={handleRowClick}
              emptyStateText="No tickets match your search or filters"
            />
          )}
        </TableCard>
      </TicketsContainer>

      <CreateTicketModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        folderName={folderName}
      />
    </TicketsWrapper>
  );
};

export default Tickets;
